import React, { MouseEventHandler, forwardRef, useEffect, useState } from 'react';
import FocusTrap from 'focus-trap-react';
import {
  Box,
  Avatar,
  Text,
  Overlay,
  OverlayCenter,
  OverlayBackdrop,
  IconButton,
  Icon,
  Icons,
  Tooltip,
  TooltipProvider,
  Menu,
  MenuItem,
  toRem,
  config,
  Line,
  PopOut,
  RectCords,
  Chip,
} from 'folds';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { JoinRule, Room } from 'matrix-js-sdk';
import { useAtomValue } from 'jotai';

import { useStateEvent } from '../../hooks/useStateEvent';
import { PageHeader } from '../../components/page';
import { RoomAvatar, RoomIcon } from '../../components/room-avatar';
import { UseStateProvider } from '../../components/UseStateProvider';
import { RoomTopicViewer } from '../../components/room-topic-viewer';
import { StateEvent } from '../../../types/matrix/room';
import { useMatrixClient } from '../../hooks/useMatrixClient';
import { useRoom } from '../../hooks/useRoom';
import { useSetSetting } from '../../state/hooks/settings';
import { settingsAtom } from '../../state/settings';
import { useSpaceOptionally } from '../../hooks/useSpace';
import { getHomeSearchPath, getSpaceSearchPath, withSearchParam } from '../../pages/pathUtils';
import { getCanonicalAliasOrRoomId, isRoomAlias, mxcUrlToHttp } from '../../utils/matrix';
import { _SearchPathSearchParams } from '../../pages/paths';
import * as css from './RoomViewHeader.css';
import { useRoomUnread } from '../../state/hooks/unread';
import { usePowerLevelsAPI, usePowerLevelsContext } from '../../hooks/usePowerLevels';
import { markAsRead } from '../../../client/action/notifications';
import { roomToUnreadAtom } from '../../state/room/roomToUnread';
import { openInviteUser, toggleRoomSettings } from '../../../client/action/navigation';
import { copyToClipboard } from '../../utils/dom';
import { LeaveRoomPrompt } from '../../components/leave-room-prompt';
import { useRoomAvatar, useRoomName, useRoomTopic } from '../../hooks/useRoomMeta';
import { mDirectAtom } from '../../state/mDirectList';
import { ScreenSize, useScreenSizeContext } from '../../hooks/useScreenSize';
import { stopPropagation } from '../../utils/keyboard';
import { getMatrixToRoom } from '../../plugins/matrix-to';
import { getViaServers } from '../../plugins/via-servers';
import { BackRouteHandler } from '../../components/BackRouteHandler';
import { useMediaAuthentication } from '../../hooks/useMediaAuthentication';
import { getStateEvent } from '../../utils/room';
import { Membership } from '../../../types/matrix/room';

import { timeDayMonthYear, timeHourMinute } from '../../utils/time';
import { SimpleMembers } from './SimpleMembers';
import { useRoomMembers } from '../../hooks/useRoomMembers';
import { CaseStatusDropdown } from '../curve/CaseStatusDropdown';
import { CaseHeaderMoreMenu } from '../curve/CaseHeaderMoreMenu';
import { useRoomProcessInfo } from '../../state/room/roomProcessInfo';
import { updateRoomProcessInfo } from '../../../client/action/navigation';

type RoomMenuProps = {
  room: Room;
  requestClose: () => void;
};
const RoomMenu = forwardRef<HTMLDivElement, RoomMenuProps>(({ room, requestClose }, ref) => {
  const mx = useMatrixClient();
  const { t } = useTranslation();
  const unread = useRoomUnread(room.roomId, roomToUnreadAtom);
  const powerLevels = usePowerLevelsContext();
  const { getPowerLevel, canDoAction } = usePowerLevelsAPI(powerLevels);
  const canInvite = canDoAction('invite', getPowerLevel(mx.getUserId() ?? ''));

  const handleMarkAsRead = () => {
    markAsRead(mx, room.roomId);
    requestClose();
  };

  const handleInvite = () => {
    openInviteUser(room.roomId);
    requestClose();
  };

  const handleCopyLink = () => {
    const roomIdOrAlias = getCanonicalAliasOrRoomId(mx, room.roomId);
    const viaServers = isRoomAlias(roomIdOrAlias) ? undefined : getViaServers(room);
    copyToClipboard(getMatrixToRoom(roomIdOrAlias, viaServers));
    requestClose();
  };

  const handleRoomSettings = () => {
    toggleRoomSettings(room.roomId);
    requestClose();
  };

  return (
    <Menu ref={ref} style={{ maxWidth: toRem(160), width: '100vw' }}>
      <Box direction="Column" gap="100" style={{ padding: config.space.S100 }}>
        <MenuItem
          onClick={handleMarkAsRead}
          size="300"
          after={<Icon size="100" src={Icons.CheckTwice} />}
          radii="300"
          disabled={!unread}
        >
          <Text style={{ flexGrow: 1 }} as="span" size="T300" truncate>
            {t('RoomHeader.mark_as_read')}
          </Text>
        </MenuItem>
      </Box>
      <Line variant="Surface" size="300" />
      <Box direction="Column" gap="100" style={{ padding: config.space.S100 }}>
        <MenuItem
          onClick={handleInvite}
          variant="Primary"
          fill="None"
          size="300"
          after={<Icon size="100" src={Icons.UserPlus} />}
          radii="300"
          disabled={!canInvite}
        >
          <Text style={{ flexGrow: 1 }} as="span" size="T300" truncate>
            {t('RoomHeader.invite')}
          </Text>
        </MenuItem>
        { /* <MenuItem
          onClick={handleCopyLink}
          size="300"
          after={<Icon size="100" src={Icons.Link} />}
          radii="300"
        >
          <Text style={{ flexGrow: 1 }} as="span" size="T300" truncate>
            Copy Link
          </Text>
        </MenuItem> */ }
        { /* <MenuItem
          onClick={handleRoomSettings}
          size="300"
          after={<Icon size="100" src={Icons.Setting} />}
          radii="300"
        >
          <Text style={{ flexGrow: 1 }} as="span" size="T300" truncate>
            Room Settings
          </Text>
        </MenuItem> */ }
      </Box>
      <Line variant="Surface" size="300" />
      <Box direction="Column" gap="100" style={{ padding: config.space.S100 }}>
        <UseStateProvider initial={false}>
          {(promptLeave, setPromptLeave) => (
            <>
              <MenuItem
                onClick={() => setPromptLeave(true)}
                variant="Critical"
                fill="None"
                size="300"
                after={<Icon size="100" src={Icons.ArrowGoLeft} />}
                radii="300"
                aria-pressed={promptLeave}
              >
                <Text style={{ flexGrow: 1 }} as="span" size="T300" truncate>
                  {t('RoomHeader.leave_room')}
                </Text>
              </MenuItem>
              {promptLeave && (
                <LeaveRoomPrompt
                  roomId={room.roomId}
                  onDone={requestClose}
                  onCancel={() => setPromptLeave(false)}
                />
              )}
            </>
          )}
        </UseStateProvider>
      </Box>
    </Menu>
  );
});

type RoomViewHeaderProps = {
  setHasRoomSelected: React.Dispatch<React.SetStateAction<boolean>>;
  setRoomClosed: React.Dispatch<React.SetStateAction<boolean>>;
};

const STATUS_ITEMS = [
  { id: 'pending', label: '待處理' },
  { id: 'in_progress', label: '處理中' },
  { id: 'closed', label: '已結案' },
];

const processInfoToStatusId = (info: { process_action?: number; process_state?: number } | null | undefined): string => {
  if (!info || typeof info.process_action !== 'number') return 'pending';
  if (info.process_action === 3) return 'closed';
  if (info.process_action === 0 && info.process_state === 1) return 'in_progress';
  if (info.process_action === 1 || info.process_action === 2) return 'in_progress';
  return 'pending';
};

export function RoomViewHeader({ setHasRoomSelected, setRoomClosed }: RoomViewHeaderProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const mx = useMatrixClient();
  const useAuthentication = useMediaAuthentication();
  const screenSize = useScreenSizeContext();
  const room = useRoom();
  const space = useSpaceOptionally();
  const [menuAnchor, setMenuAnchor] = useState<RectCords>();
  const mDirects = useAtomValue(mDirectAtom);

  const encryptionEvent = useStateEvent(room, StateEvent.RoomEncryption);
  const ecryptedRoom = !!encryptionEvent;
  const avatarMxc = useRoomAvatar(room, mDirects.has(room.roomId));
  const name = useRoomName(room);
  const topic = useRoomTopic(room);
  const avatarUrl = avatarMxc ? mxcUrlToHttp(mx, avatarMxc, useAuthentication, 96, 96, 'crop') ?? undefined : undefined;

  const members = useRoomMembers(mx, room.roomId);
  const { info: processInfo, fetchInfo } = useRoomProcessInfo(mx, room.roomId);

  useEffect(() => { fetchInfo(); }, [fetchInfo]);

  const currentStatusId = processInfoToStatusId(processInfo as { process_action?: number; process_state?: number } | null);

  const handleStatusChange = async (newId: string) => {
    let process_action = 0;
    let process_state = 0;
    if (newId === 'in_progress') { process_action = 0; process_state = 1; }
    if (newId === 'closed') { process_action = 3; process_state = 2; }
    try {
      await mx.http.authedRequest(
        'PUT' as any,
        `/rooms/${room.roomId}/process_info`,
        undefined,
        { process_action, process_state }
      );
      updateRoomProcessInfo(room.roomId);
      fetchInfo();
    } catch (e) {
      console.error('Failed to update process info', e);
    }
  };

  const setPeopleDrawer = useSetSetting(settingsAtom, 'isPeopleDrawer');

  const createEvent = getStateEvent(room, StateEvent.RoomCreate);
  const ts = createEvent?.getTs();

  const handleSearchClick = () => {
    const searchParams: _SearchPathSearchParams = {
      rooms: room.roomId,
    };
    const path = space
      ? getSpaceSearchPath(getCanonicalAliasOrRoomId(mx, space.roomId))
      : getHomeSearchPath();
    navigate(withSearchParam(path, searchParams));
  };

  const handleOpenMenu: MouseEventHandler<HTMLButtonElement> = (evt) => {
    setMenuAnchor(evt.currentTarget.getBoundingClientRect());
  };

  const moreMenuItems = [
    /* TMS: disabled
    {
      id: 'invite',
      label: t('RoomHeader.invite'),
      onClick: () => openInviteUser(room.roomId),
    }, 
    */
    {
      id: 'members',
      label: t('RoomHeader.members'),
      onClick: () => setPeopleDrawer((v) => !v),
    },
    /* TMS: disbaled
    {
      id: 'mark-read',
      label: t('RoomHeader.mark_as_read'),
      onClick: () => markAsRead(mx, room.roomId),
    },
    */
  ];

  return (
    <PageHeader balance={screenSize === ScreenSize.Mobile}>
      <Box grow="Yes" gap="300">
        {screenSize === ScreenSize.Mobile && (
          <BackRouteHandler>
            {(onBack) => (
              <Box shrink="No" alignItems="Center">
                <IconButton onClick={onBack}>
                  <Icon src={Icons.ArrowLeft} />
                </IconButton>
              </Box>
            )}
          </BackRouteHandler>
        )}
        <Box grow="Yes" alignItems="Center" gap="300">
          {screenSize !== ScreenSize.Mobile && (
            <Avatar size="300">
              <RoomAvatar
                roomId={room.roomId}
                src={avatarUrl}
                alt={name}
                renderFallback={() => (
                  <RoomIcon
                    size="200"
                    joinRule={room.getJoinRule() ?? JoinRule.Restricted}
                    filled
                  />
                )}
              />
            </Avatar>
          )}
          <Box direction="Column">
            <Text size={topic ? 'H5' : 'H3'} truncate>
              {name}
            </Text>
            {ts && <Text size='T200' truncate>{`${timeDayMonthYear(ts)} ${timeHourMinute(ts)}`}</Text>}
            {topic && (
              <UseStateProvider initial={false}>
                {(viewTopic, setViewTopic) => (
                  <>
                    <Overlay open={viewTopic} backdrop={<OverlayBackdrop />}>
                      <OverlayCenter>
                        <FocusTrap
                          focusTrapOptions={{
                            initialFocus: false,
                            clickOutsideDeactivates: true,
                            onDeactivate: () => setViewTopic(false),
                            escapeDeactivates: stopPropagation,
                          }}
                        >
                          <RoomTopicViewer
                            name={name}
                            topic={topic}
                            requestClose={() => setViewTopic(false)}
                          />
                        </FocusTrap>
                      </OverlayCenter>
                    </Overlay>
                    <Text
                      as="button"
                      type="button"
                      onClick={() => setViewTopic(true)}
                      className={css.HeaderTopic}
                      size="T200"
                      priority="300"
                      truncate
                    >
                      {topic}
                    </Text>
                  </>
                )}
              </UseStateProvider>
            )}
          </Box>
        </Box>

        {/* 案件狀態下拉 */}
        <CaseStatusDropdown
          items={STATUS_ITEMS}
          selectedId={currentStatusId}
          onChange={handleStatusChange}
          disabled
        />

        {/* TMS: has no icon <SimpleMembers key={room.roomId} room={room} members={members} /> */}

        <Box shrink="No" alignItems="Center" gap="200">
          {/* 三點選單（CaseHeaderMoreMenu） */}
          <CaseHeaderMoreMenu items={moreMenuItems} />

          {/* 關閉按鈕 */}
          <IconButton onClick={() => { setRoomClosed(true); setHasRoomSelected(false); }} style={{ backgroundColor: 'var(--curve-primary)' }}>
            <Icon size="400" src={Icons.Cross} />
          </IconButton>
        </Box>
      </Box>
    </PageHeader>
  );
}
